package top.wcpe.allincore.dsl.ir.codec

import kotlin.jvm.JvmStatic

/**
 * IR 二进制读取工具，与 [IrWriter] 对偶。
 *
 * commonMain 化（FR-UI-020 M3）：由原 jvmMain Java 平移至 commonMain 纯 Kotlin，
 * 逻辑、常量、解码行为逐一等价。静态方法以 [JvmStatic] 保 Java 可访问。
 */
object IrReader {

    /**
     * 单个 LP-Bytes32 字节段的最大长度上限（1 MiB）。
     *
     * 与 `IrDecoder.MAX_INLINE_BYTES` 取值一致：IR 内单段不透明载荷
     * （action.body / EXPR binding / derived expr 等）正常都远小于此值。
     * 上限用于在 `ByteArray(len)` 分配之前拦截畸形长度声明，避免恶意字节流
     * 通过声明 ~2GiB 长度触发 OOM。
     */
    private const val MAX_LP_BYTES32 = 1024 * 1024

    /** 读取 2B 长度 + UTF-8 字符串。 */
    @JvmStatic
    fun readLpString(buf: IrByteReader): String {
        val len = buf.readUnsignedShort()
        val bytes = ByteArray(len)
        buf.readBytes(bytes)
        return bytes.decodeToString()
    }

    /** 读取 4B 长度 + 字节数组。 */
    @JvmStatic
    fun readLpBytes32(buf: IrByteReader): ByteArray {
        val len = buf.readInt()
        if (len < 0) {
            throw IllegalStateException("LP-Bytes32 长度为负: " + len)
        }
        // 防御畸形长度：超过上限或超过缓冲区剩余可读字节，均在分配数组前拒绝，避免 OOM。
        if (len > MAX_LP_BYTES32) {
            throw IllegalStateException(
                "LP-Bytes32 长度超过上限: " + len + " > " + MAX_LP_BYTES32,
            )
        }
        val readable = buf.readableBytes()
        if (len > readable) {
            throw IllegalStateException(
                "LP-Bytes32 长度超过剩余可读字节: 声明 " + len + "，实际可读 " + readable,
            )
        }
        val data = ByteArray(len)
        buf.readBytes(data)
        return data
    }

    /**
     * 读取 valueType(u8) + 变长 payload，返回装箱对象。
     *
     * v14 起新增：
     * - [IrWriter.VT_LIST]：u16 count + 重复 readValue 元素，返回 ArrayList<Object>
     * - [IrWriter.VT_MAP]：u16 count + 重复 (LP-UTF8 key + readValue value)，返回 LinkedHashMap<String, Object>
     */
    @JvmStatic
    fun readValue(buf: IrByteReader): Any? {
        val type = buf.readUnsignedByte()
        return when (type) {
            IrWriter.VT_NULL -> null
            IrWriter.VT_BOOL -> buf.readByte().toInt() != 0
            // 编号 2（VT_INT64）已废弃，未匹配则进入 else 抛异常
            IrWriter.VT_DOUBLE -> buf.readDouble()
            IrWriter.VT_STRING -> readLpString(buf)
            IrWriter.VT_LIST -> readListValue(buf)
            IrWriter.VT_MAP -> readMapValue(buf)
            else -> throw IllegalStateException("未知 valueType: " + type)
        }
    }

    /** 读 List 字面量：u16 count + 重复 readValue 元素。 */
    private fun readListValue(buf: IrByteReader): List<Any?> {
        val count = buf.readUnsignedShort()
        val list = ArrayList<Any?>(count)
        for (i in 0 until count) {
            list.add(readValue(buf))
        }
        return list
    }

    /** 读 Map 字面量：u16 count + 重复 (LP-UTF8 key + readValue value)。 */
    private fun readMapValue(buf: IrByteReader): Map<String, Any?> {
        val count = buf.readUnsignedShort()
        val map = LinkedHashMap<String, Any?>(count)
        for (i in 0 until count) {
            val key = readLpString(buf)
            map[key] = readValue(buf)
        }
        return map
    }
}
